import type React from 'react';

import { Button, Link, Modal, ModalBody, ModalHeader, Text } from '@change/ds-core-ui';

import { TypedTranslateHtml, useI18n } from '@change-corgi/core-react/i18n';

import { useCancelSubscriptionConfirmationModal } from './hooks/useCancelSubscriptionConfirmationModal';

export const CancelSubscriptionConfirmationModal: React.FC<{ trigger: JSX.Element }> = ({ trigger }) => {
	const { translate } = useI18n();
	const {
		data: {
			title,
			descriptionHtmlValue,
			learnMoreLink,
			learnMoreLinkDataQa,
			descriptionHtmlDataQa,
			cancelLabel,
			confirmLabel,
			confirmDataQa,
			cancelDataQa,
			isLoading,
		},
		actions: { clickCancelSubscription, stayOnContributionsPage },
	} = useCancelSubscriptionConfirmationModal();

	return (
		<Modal
			trigger={trigger}
			onClose={() => {
				stayOnContributionsPage();
			}}
			closeButtonAriaLabel={translate('corgi.components.modal.close_button')}
			className="z-999 *:min-h-[125px]!"
			size="small"
		>
			<ModalHeader title={<Text size="x-small">{title}</Text>} />
			<ModalBody>
				{({ close }) => (
					<div className="mx-auto flex flex-col items-center">
						<Text size="medium" data-qa={descriptionHtmlDataQa}>
							<TypedTranslateHtml
								value={descriptionHtmlValue}
								replacements={{ learnMoreLink }}
								allowLinkTargetAttr
								replace={(element, { domToReact }) => {
									if (element.name !== 'a') {
										return undefined;
									}
									return (
										<Link
											href={element.attribs.href}
											target="_blank"
											rel="noopener noreferrer"
											data-qa={learnMoreLinkDataQa}
											onClick={() => {
												stayOnContributionsPage();
												close();
											}}
										>
											{domToReact(element.children)}
										</Link>
									);
								}}
							/>
						</Text>
						<div className="mt-6 flex w-full flex-col gap-4 p-1">
							<Button
								loading={isLoading}
								onClick={async () => {
									await clickCancelSubscription(() => close());
									close();
								}}
								data-qa={confirmDataQa}
								data-testid="button-confirm"
								variant="primary"
								size="small"
							>
								{confirmLabel}
							</Button>
							<Button
								onClick={() => {
									stayOnContributionsPage();
									close();
								}}
								data-qa={cancelDataQa}
								data-testid="button-cancel"
								variant="secondary"
								size="small"
							>
								{cancelLabel}
							</Button>
						</div>
					</div>
				)}
			</ModalBody>
		</Modal>
	);
};
